import {inject, Injectable} from './di';
import {Logger} from './logger';

@Injectable()
export class MediaService {
  private readonly logger = inject(Logger);

  public type: DirectType | null = null;

  private needShutdown: boolean = false;

  // NOTE: so it's supposed that audio streams are only in Radio, Wakeup, MOD sections
  // And unlike video streams, audio streams are not managed by separate service
  // So it's intended navigating through audio pages requires hardcoded way of stopping audio and navigate strictly from one page to another and vice versa
  private readonly stoppingList: {[type in DirectType]: () => (...args: any) => void | any | Promise<any>} = {
    [DirectType.IP]: () => window._tv_channel_stop,
    [DirectType.RF]: () => window._tv_channel_stop,
    [DirectType.Video]: () => window.videoCollection?.destroy,
    [DirectType.Radio]: () => window.RADIO?.close,
    [DirectType.Wakeup]: () => window.Wakeupcall?.stopWakeupAudio,
    [DirectType.Music]: () => window.MOD?.close,
    [DirectType.VOD]: () => window.VOD?.destroy,
  };

  constructor() {
    window.Media = this;
  }

  public set(param: {directType: DirectType | null}) {
    this.type = param.directType;
    this.shutdown(param);
  }

  /**
   * @param {string|object} param.directType – [все параметры перечисленны в stoppingList] – куда переходим. Зная куда переходим, знаем что стопорить
   * @param {boolean} [param.shutdown] - стопим в любом случае.
   * 									 Кейс для которого это делается:
   * 									 - смотрели VOD,
   * 									 – выключили ТВ в Standby режим,
   * 									 – включили,
   * 									 – ТВ канал играет, но картинки нет (вылетала ошибка resize).
   * 									 Обнаружено на STB-5500
   */
  public async stop(param?: {directType: DirectType | null; shutdown?: boolean}) {
    try {
      param = param || {directType: null};

      if (this.type == null || this.type === param.directType) {
        if (this.shutdown() || param.shutdown) {
          await _player_shutdown();
        }

        this.type = param.directType;
        this.shutdown(param);
      } else {
        if (
          this.type === DirectType.Video ||
          this.type === DirectType.Wakeup ||
          this.type === DirectType.Music ||
          this.type === DirectType.Radio ||
          this.type === DirectType.VOD
        ) {
          // TODO: refactor it
          // pass param to the videoCollection.destroy
          // if the function returned result, use it
          const mediaParam = this.type === DirectType.Video ? param : undefined;

          const func = this.stoppingList[this.type]();
          let result = undefined;
          if (func && typeof func === 'function') {
            result = await func(mediaParam);
          } else {
            this.logger.error(`[MediaService] Stopping list function for ${this.type} is not a function`);
          }

          if (
            (this.shutdown() || param.shutdown) &&
            (param.directType === DirectType.IP || param.directType === DirectType.RF)
          ) {
            await _player_shutdown();
          }

          result = result || param;
          this.type = result.directType;
          this.shutdown(result);
        } else {
          const func = this.stoppingList[this.type]();
          if (func && typeof func === 'function') {
            await func();
          } else {
            this.logger.error(`[MediaService] Stopping list function for ${this.type} is not a function`);
          }

          this.type = param.directType;
          this.shutdown(param);
        }
      }
    } catch (err) {
      this.logger.error(`[MediaService] unexpected error in stop function`, err);
    }
  }

  /**
   * When passed a parameter, it works as a setter.
   * Without a parameter, it works as a getter.
   */
  private shutdown(param?: {directType: DirectType | null}) {
    // TODO: (kerber) maybe on android too?
    //  because _player_shutdown are defined in tv_android.js and tv_lg.js
    if (!window.tv_lg_mark) {
      return false;
    }

    if (param) {
      if (
        param.directType === DirectType.Video ||
        param.directType === DirectType.Wakeup ||
        param.directType === DirectType.Music ||
        param.directType === DirectType.Radio ||
        param.directType === DirectType.VOD
      ) {
        this.needShutdown = true;
      } else if (param.directType === DirectType.IP || param.directType === DirectType.RF) {
        this.needShutdown = false;
      }
    } else {
      return this.needShutdown;
    }
  }
}

declare global {
  interface Window {
    Media: MediaService;
  }
  const Media: MediaService;
}

export enum DirectType {
  IP = 'ip',
  RF = 'rf',
  Video = 'video',
  Radio = 'radio',
  Wakeup = 'wakeup',
  Music = 'music',
  VOD = 'VOD',
}
